import React, { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import {
  Container,
  Card,
  Table,
  Alert,
  Image,
  Modal,
  Button,
} from "react-bootstrap"
import { apiRequest } from "../requests"
import AppNavBar from "./AppNavBar"
import { LoadingView } from "../utils/components"
import QRCode from "qrcode"
import { formatMoney, updateObjectState } from "../utils/functions"
import { constants } from "../constants"
import {
  AfrimoneyPayment,
  AfrimoneyPaymentButton,
} from "../components/AfrimoneyPayment"
import { OrangeMoneyPaymentButton } from "../components/OrangeMoneyPayment"
import OmslPayment from "../components/OmslPayment"
import ventixLogo from "../assets/images/ventix-terminal-logo-small.png"

const initData = {
  account_number: "",
  processor: "",
  otp: "",
  quantity: 1,
}

export default function TransactionReceipt() {
  const { transactionId } = useParams()
  const [isLoading, setIsLoading] = useState(true)
  const [transaction, setTransaction] = useState(null)
  const [business, setBusiness] = useState(null)
  const [employee, setEmployee] = useState(null)
  const [location, setLocation] = useState("")
  const [data, setData] = useState(initData)
  const [hasPaid, setHasPaid] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [modal, setModal] = useState({})
  const [qrCodes, setQrCodes] = useState({})

  useEffect(() => {
    fetchTransaction()
  }, [transactionId])

  useEffect(() => {
    if (transaction?.tickets) {
      generateQRCodes()
    } else if (transaction?.url) {
      generateTransactionQR()
    }
  }, [transaction])

  const fetchTransaction = async () => {
    setIsLoading(true)
    const { success, response } = await apiRequest.get(
      `api/txn/${transactionId}`
    )

    if (success) {
      setTransaction(response.transaction)
      setBusiness(response.business)
      setEmployee(response.employee)
      setLocation(response.location || "")
    } else {
      setModal({
        title: "Error",
        body: response.message || "Failed to load transaction",
        isError: true,
        success: false,
      })
      setShowModal(true)
    }
    setIsLoading(false)
  }

  const generateQRCodes = async () => {
    const codes = {}
    if (transaction.tickets) {
      for (const ticket of transaction.tickets) {
        const qrData = ticket.reference_id || ticket.number
        try {
          codes[ticket.number] = await QRCode.toDataURL(qrData)
        } catch (err) {
          console.error("Error generating QR code:", err)
        }
      }
    }
    setQrCodes(codes)
  }

  const generateTransactionQR = async () => {
    if (transaction.url) {
      try {
        const qrData = await QRCode.toDataURL(transaction.url)
        setQrCodes({ transaction: qrData })
      } catch (err) {
        console.error("Error generating QR code:", err)
      }
    }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return "-"
    const date = new Date(dateStr)
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
  }

  const submitPayment = async () => {
    setIsLoading(true)
    setShowModal(false)

    const payload = {
      account_number: data.account_number,
      processor: data.processor,
      otp: data.otp,
    }

    const { success, response } = await apiRequest.post(
      `api/txn/${transaction.id}/process-payment`,
      payload
    )

    if (success) {
      setTransaction(response.transaction)
      if (payload.processor === constants.paymentProcessors.africell) {
        setHasPaid(true)
        setModal({
          title: "Payment Initiated",
          body: response.message,
          success: true,
        })
        setShowModal(true)
        setIsLoading(false)
      } else if (response.finalized) {
        setModal({
          title: "Payment Successful!",
          body: response.message,
          success: true,
        })
        setHasPaid(false)
        setData(initData)
        setShowModal(true)
        setIsLoading(false)
      } else {
        window.location.href = response.payment_url
      }
    } else {
      setModal({
        title: "Error",
        body: response.message,
        isError: true,
        success: false,
      })
      setShowModal(true)
      setIsLoading(false)
    }
  }

  const confirmPayment = async () => {
    setIsLoading(true)
    setShowModal(false)
    const { success, response } = await apiRequest.post(
      `api/txn/${transaction.id}/pay-verify`,
      data
    )

    if (success) {
      setHasPaid(false)
      setModal({
        title: "Success",
        body: response.message,
        success: true,
      })
      setShowModal(true)
      setData(initData)
      setTransaction(response.transaction)
    } else {
      setModal({
        title: "Error",
        body: response.message,
        isError: true,
        success: false,
      })
      setShowModal(true)
    }
    setIsLoading(false)
  }

  const validateForm = () => {
    return (
      data.account_number === "" ||
      (data.processor === constants.paymentProcessors.africell &&
        data.account_number === "")
    )
  }

  useEffect(() => {
    if (transaction?.status === "Pending") {
      setHasPaid(true)
    }
  }, [transaction?.status])

  const renderPaymentButtons = () => {
    if (["Completed", "Cancelled"].includes(transaction?.status)) {
      let bg = "bg-success"

      if (transaction.status === "Cancelled") {
        bg = "bg-danger"
      }

      return (
        <Alert
          // variant="info"
          className={`text-center text-white mt-0 py-3 fw-bolder fs-5 ${bg}`}
        >
          Transaction is <strong>{transaction.status}</strong>
        </Alert>
      )
    }

    // Create a mock selectedTicket for payment components
    const selectedTicket = {
      retail_price: transaction.amount || 0,
      currency: transaction.currency || "SLE",
      payment_processors: [
        constants.paymentProcessors.africell,
        constants.paymentProcessors.orange,
      ],
    }

    return (
      <Card className="mt-4">
        <Card.Header className="text-center bg-danger text-white fw-bolder py-3">
          <h3 className="text-center">Complete Payment</h3>
        </Card.Header>

        <Card.Body className="d-flex gap-3 justify-content-center mb-3">
          <div style={{ width: "50%" }}>
            <AfrimoneyPaymentButton
              onClick={(processor) => updateObjectState(setData, { processor })}
            />
          </div>
          <div style={{ width: "50%" }}>
            <OrangeMoneyPaymentButton
              onClick={(processor) => updateObjectState(setData, { processor })}
            />
          </div>
        </Card.Body>

        {data.processor && (
          <Modal
            show={true}
            onHide={() => setData({ ...data, processor: null })}
            centered
          >
            <Modal.Header closeButton></Modal.Header>
            <Modal.Body className="p-0">
              {data.processor === constants.paymentProcessors.africell && (
                <AfrimoneyPayment
                  data={data}
                  hasPaid={hasPaid}
                  confirmPayment={confirmPayment}
                  setShowModal={setShowModal}
                  setModal={setModal}
                  selectedTicket={selectedTicket}
                  setData={setData}
                  validateForm={validateForm}
                />
              )}
              {data.processor === constants.paymentProcessors.orange && (
                <OmslPayment
                  data={data}
                  setData={setData}
                  selectedTicket={selectedTicket}
                  validateForm={validateForm}
                  submitPayment={submitPayment}
                />
              )}
            </Modal.Body>
          </Modal>
        )}
      </Card>
    )
  }

  if (!transaction) {
    return (
      <div>
        <AppNavBar />
        <LoadingView
          isLoading={isLoading}
          view={
            <Container className="mt-5">
              <Alert variant="warning">Transaction not found</Alert>
            </Container>
          }
        />
      </div>
    )
  }

  return (
    <div>
      <AppNavBar />
      <LoadingView
        isLoading={isLoading}
        view={
          <Container
            className="my-5"
            style={{ maxWidth: "600px" }}
          >
            <Card>
              <Card.Body
                className="p-4"
                style={{ fontFamily: "monospace" }}
              >
                {/* Business Logo */}
                {business?.logo && (
                  <div className="text-center mb-3 mt-4">
                    <Image
                      src={business.logo}
                      alt={business.name}
                      style={{ maxWidth: "200px", height: "auto" }}
                    />
                  </div>
                )}

                {/* Business Info */}
                <h4 className="text-center mb-0">
                  {business?.name?.toUpperCase() || "BUSINESS"}
                </h4>
                {business?.description && (
                  <p className="text-center text-muted">
                    {business.description}
                  </p>
                )}
                {location && <p className="text-center">{location}</p>}
                {business?.phone_number && (
                  <p className="text-center">+232{business.phone_number}</p>
                )}

                <hr />

                {/* Receipt Number */}
                <Table
                  borderless
                  size="sm"
                  className="mb-2"
                >
                  <tbody>
                    <tr>
                      <td>
                        <strong>Receipt #:</strong>
                      </td>
                      <td className="text-end">
                        {transaction.transaction_number || "-"}
                      </td>
                    </tr>
                  </tbody>
                </Table>

                <hr />

                {/* Vessel Info */}
                {transaction.vessel && (
                  <>
                    <Table
                      borderless
                      size="sm"
                      className="mb-2"
                    >
                      <tbody>
                        <tr>
                          <td>
                            <strong>{transaction.vessel.type}:</strong>
                          </td>
                          <td className="text-end">
                            {transaction.vessel.registration_id}
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                    <hr />
                    <Table
                      borderless
                      size="sm"
                      className="mb-2"
                    >
                      <tbody>
                        <tr>
                          <td>
                            <strong>Departs On:</strong>
                          </td>
                          <td className="text-end">
                            {formatDate(transaction.departure_time)}
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                    <hr />
                  </>
                )}

                {/* Agent */}
                <Table
                  borderless
                  size="sm"
                  className="mb-2"
                >
                  <tbody>
                    <tr>
                      <td>
                        <strong>Agent:</strong>
                      </td>
                      <td className="text-end">{employee?.full_name || "-"}</td>
                    </tr>
                  </tbody>
                </Table>

                <hr />

                {/* Enforcement Identifier */}
                {transaction.enforcement_identifier && (
                  <>
                    <Table
                      borderless
                      size="sm"
                      className="mb-2"
                    >
                      <tbody>
                        <tr>
                          <td>
                            <strong>Offender ID:</strong>
                          </td>
                          <td className="text-end">
                            {transaction.enforcement_identifier}
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                    <hr />
                  </>
                )}

                {/* Payment Method */}
                {transaction.payment_method && (
                  <>
                    <Table
                      borderless
                      size="sm"
                      className="mb-2"
                    >
                      <tbody>
                        <tr>
                          <td>
                            <strong>Method:</strong>
                          </td>
                          <td className="text-end">
                            {transaction.payment_method}
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                    <hr />
                  </>
                )}

                {/* Status */}
                <Table
                  borderless
                  size="sm"
                  className="mb-2"
                >
                  <tbody>
                    <tr>
                      <td>
                        <strong>Status:</strong>
                      </td>
                      <td className="text-end">{transaction.status}</td>
                    </tr>
                  </tbody>
                </Table>

                <hr />

                {/* Items */}
                {transaction.items && transaction.items.length > 0 && (
                  <>
                    <Table
                      size="sm"
                      className="mb-2"
                    >
                      <thead>
                        <tr>
                          <th style={{ width: "10%" }}>Qty</th>
                          <th>Item</th>
                          <th
                            className="text-end"
                            style={{ width: "30%" }}
                          >
                            Price
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {transaction.items.map((item, index) => (
                          <tr key={index}>
                            <td>{item.quantity}</td>
                            <td>{item.name}</td>
                            <td className="text-end">
                              {Number(item.price).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    <hr />
                  </>
                )}

                {/* Totals */}
                <Table
                  borderless
                  size="sm"
                  className="mb-2"
                >
                  <tbody>
                    <tr>
                      <td>
                        <strong>Sub Total:</strong>
                      </td>
                      <td className="text-end">
                        {transaction.currency}{" "}
                        {Number(transaction.sub_total || 0).toFixed(2)}
                      </td>
                    </tr>
                    {transaction.tax && (
                      <tr>
                        <td>
                          <strong>
                            {transaction.tax.name} ({transaction.tax.percentage}
                            %):
                          </strong>
                        </td>
                        <td className="text-end">
                          {transaction.currency}{" "}
                          {Number(transaction.tax.amount).toFixed(2)}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>

                <hr />

                <Table
                  borderless
                  size="sm"
                  className="mb-2"
                >
                  <tbody>
                    <tr>
                      <td>
                        <strong>TOTAL:</strong>
                      </td>
                      <td className="text-end">
                        <strong>
                          {transaction.currency}{" "}
                          {Number(transaction.amount || 0).toFixed(2)}
                        </strong>
                      </td>
                    </tr>
                  </tbody>
                </Table>

                <hr />

                {/* Issued Date */}
                <Table
                  borderless
                  size="sm"
                  className="mb-4"
                >
                  <tbody>
                    <tr>
                      <td>
                        <strong>Issued On:</strong>
                      </td>
                      <td className="text-end">
                        {formatDate(transaction.timestamp)}
                      </td>
                    </tr>
                  </tbody>
                </Table>

                {/* QR Codes */}
                {transaction.tickets && transaction.tickets.length > 0 && (
                  <div className="mt-4">
                    <h5 className="text-center mb-3">
                      YOUR TICKET(S) QR CODE(S)
                    </h5>
                    <hr />
                    {transaction.tickets.map((ticket, index) => (
                      <div
                        key={index}
                        className="text-center mb-4"
                      >
                        {qrCodes[ticket.number] && (
                          <>
                            <Image
                              src={qrCodes[ticket.number]}
                              alt={`QR Code for ticket ${ticket.reference_id}`}
                              style={{ maxWidth: "200px" }}
                            />
                            <p className="mt-2 mb-0">
                              <strong>Ticket #</strong>
                            </p>
                            <p>{ticket.reference_id || "N/A"}</p>
                            {ticket.vessel_seat_number && (
                              <p>
                                <strong>Seat:</strong>{" "}
                                {ticket.vessel_seat_number}
                              </p>
                            )}
                          </>
                        )}
                        {index < transaction.tickets.length - 1 && <hr />}
                      </div>
                    ))}
                  </div>
                )}

                {/* Footer Logo */}
                <div className="text-center my-4">
                  <Image
                    src={ventixLogo}
                    alt="Ventix"
                    style={{ maxWidth: "300px" }}
                  />
                </div>
              </Card.Body>
            </Card>

            {/* Payment Options */}
            {renderPaymentButtons()}

            {/* Modal for messages */}
            <Modal
              show={showModal}
              onHide={() => setShowModal(false)}
              centered
              className={`${
                modal.success ? "border-success" : "border-danger"
              } border-1`}
            >
              <Modal.Header
                className={`${
                  modal.success ? "bg-success" : "bg-danger"
                } py-3 text-white fw-bolder`}
                closeButton
              >
                <Modal.Title>{modal.title}</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p>{modal.body}</p>
              </Modal.Body>
              <Modal.Footer>
                {modal.interactive ? (
                  <>
                    <Button
                      variant="secondary"
                      onClick={() => setShowModal(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      className="text-white"
                      onClick={() => {
                        setShowModal(false)
                        submitPayment()
                      }}
                    >
                      Confirm
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="primary"
                    className="text-white"
                    onClick={() => setShowModal(false)}
                  >
                    Close
                  </Button>
                )}
              </Modal.Footer>
            </Modal>
          </Container>
        }
      />
    </div>
  )
}
